import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquarePlus, Paperclip, Trash2 } from 'lucide-react';

import { documentsAPI } from '../../lib/api';
import {
  ajouterObservation,
  cleFrais,
  listerObservations,
  messageErreurFrais,
  supprimerObservation,
} from '../../lib/expensesApi';
import { formatDateHeure } from '../../lib/formats';
import { libelle, optionsLibelles, referenceLisible, texteSur } from '../../lib/libelles';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { FieldError } from '../ui/field-error';
import { Label } from '../ui/label';
import { LoadingState } from '../ui/loading-state';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

/**
 * Le fil des observations d'un camion, d'un trajet ou d'un convoi.
 *
 * Il réunit ce qu'on y a écrit à la main et ce que le terrain a déjà dit:
 * incidents du chauffeur, pannes, notes de changement d'étape, motifs des
 * frais supplémentaires. Du plus récent au plus ancien. Seules les
 * observations saisies à la main se suppriment, par leur auteur ou la direction.
 *
 * Props: `entityType` ('truck' | 'trip' | 'convoy'), `entityId`, et
 * `colonneEtroite` quand le fil loge dans la colonne de droite d'une fiche
 * (360 px à partir de xl): saisie sur une seule colonne, boutons pleine
 * largeur, état vide resserré.
 */

const PAGE = 50;
const NATURES = ['CONSTAT', 'INCIDENT', 'RAPPEL', 'FRAIS', 'AUTRE'];

const TON_SOURCE = {
  OBSERVATION: 'muted',
  INCIDENT: 'warning',
  PANNE: 'destructive',
  ETAPE: 'info',
  FRAIS_SUPP: 'warning',
};

export default function ObservationsPanel({ entityType, entityId, colonneEtroite = false }) {
  const [elements, setElements] = useState([]);
  const [total, setTotal] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [texte, setTexte] = useState('');
  const [nature, setNature] = useState('CONSTAT');
  const [piece, setPiece] = useState(null);
  const [erreurs, setErreurs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [televersement, setTeleversement] = useState(false);
  const [cle, setCle] = useState(() => cleFrais('observation'));
  const fichierRef = useRef(null);

  const charger = useCallback(async () => {
    if (!entityType || !entityId) return;
    setChargement(true);
    setErreur('');
    try {
      const resultat = await listerObservations(entityType, entityId, { limit: PAGE, offset: 0 });
      setElements(resultat.items);
      setTotal(resultat.total);
    } catch (error) {
      setErreur(messageErreurFrais(error, 'Les observations n’ont pas pu être chargées.'));
    } finally {
      setChargement(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    charger();
  }, [charger]);
  useRafraichirSur(['depenses', 'camions', 'trajets', 'convois'], charger);

  const chargerPlus = async () => {
    try {
      const resultat = await listerObservations(entityType, entityId, { limit: PAGE, offset: elements.length });
      setElements((ancien) => [...ancien, ...resultat.items]);
      setTotal(resultat.total);
    } catch (error) {
      setErreur(messageErreurFrais(error, 'Les observations n’ont pas pu être chargées.'));
    }
  };

  const joindre = async (fichier) => {
    if (!fichier) return;
    if (fichier.size > 10 * 1024 * 1024) {
      setErreurs({ document: 'Fichier trop lourd : 10 Mo au maximum.' });
      return;
    }
    setTeleversement(true);
    try {
      const envoye = await documentsAPI.upload(fichier, 'OTHER', { capture_source: 'web_operator' });
      setPiece({ id: envoye?.id, nom: fichier.name });
      setErreurs({});
      setCle(cleFrais('observation'));
    } catch (error) {
      setErreurs({ document: 'La photo n’a pas pu être envoyée. Réessayez dans un instant.' });
    } finally {
      setTeleversement(false);
    }
  };

  const ajouter = async () => {
    if (!texte.trim()) {
      setErreurs({ text: 'Écrivez l’observation avant de l’ajouter.' });
      return;
    }
    setErreurs({});
    setErreur('');
    setEnvoi(true);
    try {
      const corps = { entity_type: entityType, entity_id: entityId, kind: nature, text: texte.trim() };
      if (piece?.id) corps.document_id = piece.id;
      await ajouterObservation(corps, cle);
      setTexte('');
      setPiece(null);
      setCle(cleFrais('observation'));
      if (fichierRef.current) fichierRef.current.value = '';
      await charger();
    } catch (error) {
      setErreur(messageErreurFrais(error, 'L’observation n’a pas pu être enregistrée.'));
    } finally {
      setEnvoi(false);
    }
  };

  const retirer = async (element) => {
    if (!window.confirm('Supprimer cette observation ?')) return;
    try {
      await supprimerObservation(element.id);
      setElements((ancien) => ancien.filter((e) => e.id !== element.id));
      setTotal((ancien) => Math.max(0, ancien - 1));
    } catch (error) {
      setErreur(messageErreurFrais(error, 'L’observation n’a pas pu être supprimée.'));
    }
  };

  return (
    <div className="space-y-4" data-testid="observations-panel">
      <div className="space-y-3 rounded-lg border bg-card p-4">
        <div className={cn('grid gap-3 sm:grid-cols-[12rem_1fr]', colonneEtroite && 'xl:grid-cols-1')}>
          <div>
            <Label htmlFor="observation-nature">Nature</Label>
            <select
              id="observation-nature"
              className="mt-1 block w-full rounded-md border bg-background p-2 text-sm"
              value={nature}
              onChange={(event) => setNature(event.target.value)}
            >
              {optionsLibelles('type_observation', NATURES).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="observation-texte">Observation</Label>
            <Textarea
              id="observation-texte"
              rows={2}
              maxLength={2000}
              className="mt-1"
              placeholder="Ex. pneu arrière usé, à changer au retour."
              value={texte}
              onChange={(event) => {
                setTexte(event.target.value);
                setErreurs((ancien) => ({ ...ancien, text: undefined }));
                setCle(cleFrais('observation'));
              }}
              aria-invalid={Boolean(erreurs.text)}
            />
            <FieldError show={Boolean(erreurs.text)}>{erreurs.text}</FieldError>
          </div>
        </div>
        <div className={cn('flex flex-wrap items-center justify-between gap-2', colonneEtroite && 'xl:flex-col xl:items-stretch')}>
          <div className="flex min-w-0 items-center gap-2">
            <input
              ref={fichierRef}
              type="file"
              accept="image/*,application/pdf"
              className="sr-only"
              aria-label="Joindre une photo"
              disabled={televersement}
              onChange={(event) => joindre(event.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(colonneEtroite && 'xl:flex-1')}
              disabled={televersement}
              onClick={() => fichierRef.current?.click()}
            >
              {televersement ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Paperclip className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {piece ? 'Changer la photo' : 'Joindre une photo'}
            </Button>
            {piece && <span className="truncate text-sm text-muted-foreground">{piece.nom}</span>}
          </div>
          <Button
            type="button"
            className={cn(colonneEtroite && 'xl:w-full')}
            onClick={ajouter}
            disabled={envoi || televersement}
          >
            {envoi ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <MessageSquarePlus className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Ajouter l’observation
          </Button>
        </div>
        <FieldError show={Boolean(erreurs.document)}>{erreurs.document}</FieldError>
      </div>

      {erreur && (
        <p role="alert" className="text-sm text-destructive">
          {erreur}
        </p>
      )}

      {chargement ? (
        <LoadingState shape="table" />
      ) : elements.length === 0 && !erreur ? (
        <EmptyState
          variant="empty"
          icon={MessageSquarePlus}
          title="Aucune observation pour le moment"
          description={
            colonneEtroite
              ? 'Notez un constat, un incident ou un rappel. Les incidents et les pannes signalés s’affichent aussi ici.'
              : 'Notez ici ce que vous constatez : un pneu usé, un incident, un rappel. Les incidents des chauffeurs, les pannes et les notes de changement d’étape s’affichent aussi dans ce fil.'
          }
          className={colonneEtroite ? 'px-4 py-6' : undefined}
        />
      ) : (
        <ul className="divide-y rounded-xl border bg-card" aria-label="Observations">
          {elements.map((element) => (
            <li key={element.id} className="flex items-start justify-between gap-3 p-3" data-testid="observation">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant={TON_SOURCE[element.source] || 'muted'}>
                    {element.source === 'OBSERVATION' && element.kind
                      ? libelle('type_observation', element.kind)
                      : libelle('source_observation', element.source)}
                  </Badge>
                  <span>{formatDateHeure(element.occurred_at)}</span>
                  {element.author_name && <span>· {texteSur(element.author_name, '')}</span>}
                  {entityType !== 'truck' && element.truck_plate && <span>· {element.truck_plate}</span>}
                  {entityType !== 'trip' && element.trip_ref && <span>· {referenceLisible(element.trip_ref, '')}</span>}
                </div>
                <p className="whitespace-pre-wrap break-words text-sm">{texteSur(element.text, '—')}</p>
              </div>
              {element.can_delete && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-destructive hover:text-destructive"
                  onClick={() => retirer(element)}
                  aria-label="Supprimer cette observation"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!chargement && total > elements.length && (
        <div className="text-center">
          <Button type="button" variant="outline" size="sm" onClick={chargerPlus}>
            Voir les observations plus anciennes ({total - elements.length})
          </Button>
        </div>
      )}
    </div>
  );
}
